import { AdminRum, InventoryRun, InventoryRunItem } from '../../types';

// --- Run helpers ---

export function isRunActive(run: InventoryRun): boolean {
  return !!run.start_date && !run.end_date;
}

export function sortInventoryRuns(runs: InventoryRun[]): InventoryRun[] {
  return [...runs].sort((a, b) => {
    if (isRunActive(a) && !isRunActive(b)) return -1;
    if (!isRunActive(a) && isRunActive(b)) return 1;

    const aDate = a.start_date ? new Date(a.start_date).getTime() : 0;
    const bDate = b.start_date ? new Date(b.start_date).getTime() : 0;
    return bDate - aDate;
  });
}

export function formatRunLabel(run: InventoryRun): string {
  const startDate = run.start_date
    ? new Date(run.start_date).toLocaleDateString()
    : 'No date';

  if (isRunActive(run)) {
    return `[ACTIVE] Started ${startDate}`;
  }

  const endDate = run.end_date
    ? new Date(run.end_date).toLocaleDateString()
    : '';
  return `${startDate} — ${endDate}`;
}

// --- Report generation ---

export function getRumsNeedingDeactivation(
  allRums: AdminRum[],
  runItems: InventoryRunItem[],
): AdminRum[] {
  const rumIdsInRun = new Set(runItems.map(item => String(item.rum)));
  return allRums.filter(rum => rum.is_available && !rumIdsInRun.has(String(rum.id)));
}

export function getRumsNeedingReactivation(
  allRums: AdminRum[],
  runItems: InventoryRunItem[],
): AdminRum[] {
  const rumIdsInRun = new Set(runItems.map(item => String(item.rum)));
  return allRums.filter(rum => !rum.is_available && rumIdsInRun.has(String(rum.id)));
}

export function getDaysSinceAddedReport(
  allRums: AdminRum[],
  runItems: InventoryRunItem[],
): Array<{ rum: AdminRum; days: number }> {
  const rumIdsInRun = new Set(runItems.map(item => String(item.rum)));
  return allRums
    .filter(rum => rumIdsInRun.has(String(rum.id)))
    .map(rum => ({ rum, days: daysSince(rum.date_added) }))
    .sort((a, b) => b.days - a.days);
}

export function getInventoryListReport(
  allRums: AdminRum[],
  runItems: InventoryRunItem[],
): Array<{ rum: AdminRum; count: number }> {
  const rumById = new Map(allRums.map(rum => [String(rum.id), rum]));

  // In case a rum is represented by multiple run items, we sum counts.
  const countsByRumId = new Map<string, number>();
  for (const item of runItems) {
    if (item.rum == null) continue;
    const rumId = String(item.rum);
    countsByRumId.set(rumId, (countsByRumId.get(rumId) ?? 0) + item.count);
  }

  const results: Array<{ rum: AdminRum; count: number }> = [];
  countsByRumId.forEach((count, rumId) => {
    const rum = rumById.get(rumId);
    if (rum) results.push({ rum, count });
  });

  return results.sort((a, b) => (a.rum.inventory_id ?? '').localeCompare(b.rum.inventory_id ?? ''));
}

export function getPossibleReorderReport(
  allRums: AdminRum[],
  inventoryRuns: InventoryRun[],
  allRunItems: InventoryRunItem[],
  currentRunId: string,
): Array<{ rum: AdminRum; previousCount: number }> {
  const currentRun = inventoryRuns.find(r => String(r.id) === String(currentRunId));
  if (!currentRun) return [];

  const currentTime =
    (currentRun.start_date ? new Date(currentRun.start_date).getTime() : 0) ||
    (currentRun.end_date ? new Date(currentRun.end_date).getTime() : 0);

  const runTimes = inventoryRuns
    .filter(r => String(r.id) !== String(currentRunId))
    .map(r => {
      const t =
        (r.start_date ? new Date(r.start_date).getTime() : 0) ||
        (r.end_date ? new Date(r.end_date).getTime() : 0);
      return { run: r, t };
    })
    // If a run has no date, it should never be treated as "previous" relative to a dated run.
    .filter(({ t }) => t > 0);

  const previousRun = currentTime
    ? runTimes.filter(({ t }) => t < currentTime).sort((a, b) => b.t - a.t)[0]?.run
    : runTimes.sort((a, b) => b.t - a.t)[0]?.run;

  if (!previousRun) return [];

  const previousRunId = String(previousRun.id);

  const rumById = new Map(allRums.map(rum => [String(rum.id), rum]));
  const previousCountsByRumId = new Map<string, number>();
  const currentCountsByRumId = new Map<string, number>();

  for (const item of allRunItems) {
    if (item.rum == null) continue;
    const rumId = String(item.rum);
    const invRunId = String(item.inventory_run ?? '');
    if (!invRunId) continue;

    if (invRunId === previousRunId) {
      previousCountsByRumId.set(
        rumId,
        (previousCountsByRumId.get(rumId) ?? 0) + item.count,
      );
    }

    if (invRunId === String(currentRunId)) {
      currentCountsByRumId.set(rumId, (currentCountsByRumId.get(rumId) ?? 0) + item.count);
    }
  }

  const results: Array<{ rum: AdminRum; previousCount: number }> = [];
  previousCountsByRumId.forEach((previousCount, rumId) => {
    if (previousCount < 1) return;
    const currentCount = currentCountsByRumId.get(rumId) ?? 0;
    if (currentCount > 0) return;

    const rum = rumById.get(rumId);
    if (rum) results.push({ rum, previousCount });
  });

  return results.sort((a, b) => (a.rum.inventory_id ?? '').localeCompare(b.rum.inventory_id ?? ''));
}

// --- CSV generation & download ---

export function downloadCsv(filename: string, csvContent: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function generateDeactivationCsv(rums: AdminRum[]): string {
  const header = 'Inventory ID,Name,Available';
  const rows = rums.map(rum =>
    `${rum.inventory_id ?? ''},${escapeCsvField(rum.bevager_name)},Yes`,
  );
  return [header, ...rows].join('\n');
}

export function generateReactivationCsv(rums: AdminRum[]): string {
  const header = 'Inventory ID,Name,Available';
  const rows = rums.map(rum =>
    `${rum.inventory_id ?? ''},${escapeCsvField(rum.bevager_name)},No`,
  );
  return [header, ...rows].join('\n');
}

export function generateDaysSinceAddedCsv(
  data: Array<{ rum: AdminRum; days: number }>,
): string {
  const header = 'Inventory ID,Name,Date Added,Days Since Added';
  const rows = data.map(({ rum, days }) =>
    `${rum.inventory_id ?? ''},${escapeCsvField(rum.bevager_name)},${rum.date_added},${days}`,
  );
  return [header, ...rows].join('\n');
}

export function generateInventoryListCsv(
  data: Array<{ rum: AdminRum; count: number }>,
): string {
  const header = 'Inventory ID,Name,Country,Price,Bottle Count';
  const rows = data.map(({ rum, count }) =>
    `${rum.inventory_id ?? ''},${escapeCsvField(rum.bevager_name)},${escapeCsvField(
      rum.origin_country ?? '',
    )},${parseFloat(rum.price.toString()).toFixed(0)},${count}`,
  );
  return [header, ...rows].join('\n');
}

export function generatePossibleReorderCsv(
  data: Array<{ rum: AdminRum; previousCount: number }>,
): string {
  const header = 'Inventory ID,Name,Previous Bottle Count';
  const rows = data.map(({ rum, previousCount }) =>
    `${rum.inventory_id ?? ''},${escapeCsvField(rum.bevager_name)},${previousCount}`,
  );
  return [header, ...rows].join('\n');
}

// --- Internal helpers ---

function daysSince(dateString: string): number {
  const diffMs = Date.now() - new Date(dateString).getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function escapeCsvField(field: string): string {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}
